import KeyOutlined from '@mui/icons-material/KeyOutlined'
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined'
import TuneOutlined from '@mui/icons-material/TuneOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import {
  useCreateUser,
  useDeleteUser,
  useResetUserPassword,
  useRoles,
  useSetUserRoles,
  useSetUserScope,
  useUnlockUser,
  useUpdateUser,
  useUsers,
  type AdminUser,
} from '../../api/admin'
import { describeError } from '../../api/client'
import { useMyProjects, useSites } from '../../api/hooks'
import { useAuth } from '../../auth/useAuth'
import { CrudTable, type CrudField, type FormValues } from '../../components/CrudTable'
import { PageHeader } from '../../components/PageHeader'
import { ActiveChip } from '../../components/StatusChip'

const SCOPES = [
  { value: 'COMPANY', label: 'Whole company' },
  { value: 'PROJECT', label: 'Assigned projects' },
  { value: 'SITE', label: 'Assigned sites' },
  { value: 'OWN', label: 'Own records only' },
]

/**
 * User administration: who exists, what they may do, and which sites they may see.
 *
 * <p>Roles and scope are separate actions rather than fields on the edit form, because
 * both invalidate the tokens the person is currently holding - a change that should be
 * deliberate, not a side effect of correcting a phone number.</p>
 */
export function UsersPage() {
  const { can, user: me } = useAuth()
  const [page, setPage] = useState(0)
  const users = useUsers({ page, size: 50 })
  const roles = useRoles()

  const create = useCreateUser()
  const update = useUpdateUser()
  const remove = useDeleteUser()
  const setRoles = useSetUserRoles()
  const setScope = useSetUserScope()
  const resetPassword = useResetUserPassword()
  const unlock = useUnlockUser()

  const [access, setAccess] = useState<AdminUser | null>(null)
  const [credential, setCredential] = useState<{ username: string; password: string } | null>(null)
  const [actionError, setActionError] = useState<unknown>(null)

  const canCreate = can('USER:CREATE')
  const canUpdate = can('USER:UPDATE')
  const canDelete = can('USER:DELETE')
  const canAssign = can('USER:ASSIGN_ROLE') || can('USER:ASSIGN_SCOPE')
  const canReset = can('USER:RESET_PASSWORD')

  const roleOptions = (roles.data ?? []).map((r) => ({ value: r.id, label: r.name }))

  const createFields: CrudField[] = [
    { name: 'username', label: 'Username', required: true, lockedOnEdit: true },
    { name: 'fullName', label: 'Full name', required: true },
    { name: 'email', label: 'Email' },
    { name: 'mobile', label: 'Mobile' },
    { name: 'employeeCode', label: 'Employee code' },
    { name: 'designation', label: 'Designation' },
    {
      name: 'dataScope',
      label: 'Data scope',
      type: 'select',
      required: true,
      defaultValue: 'SITE',
      options: SCOPES,
      helperText: 'How far this person can see',
    },
    {
      name: 'roleId',
      label: 'Role',
      type: 'select',
      options: roleOptions,
      helperText: 'More roles can be added afterwards',
    },
    {
      name: 'password',
      label: 'Initial password',
      type: 'password',
      helperText: 'Leave blank and one is generated and shown once',
    },
  ]

  const editFields: CrudField[] = [
    { name: 'username', label: 'Username', lockedOnEdit: true },
    { name: 'fullName', label: 'Full name', required: true },
    { name: 'email', label: 'Email' },
    { name: 'mobile', label: 'Mobile' },
    { name: 'employeeCode', label: 'Employee code' },
    { name: 'designation', label: 'Designation' },
    { name: 'dataScope', label: 'Data scope', type: 'select', options: SCOPES },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  async function handleCreate(values: FormValues) {
    const result = await create.mutateAsync({
      username: String(values.username ?? '').trim(),
      fullName: String(values.fullName ?? '').trim(),
      email: values.email ? String(values.email) : undefined,
      mobile: values.mobile ? String(values.mobile) : undefined,
      employeeCode: values.employeeCode ? String(values.employeeCode) : undefined,
      designation: values.designation ? String(values.designation) : undefined,
      password: values.password ? String(values.password) : undefined,
      dataScope: (values.dataScope as AdminUser['dataScope']) ?? 'SITE',
      roleIds: values.roleId ? [String(values.roleId)] : [],
    })
    if (result.generatedPassword) {
      setCredential({ username: result.username, password: result.generatedPassword })
    }
  }

  async function handleReset(user: AdminUser) {
    setActionError(null)
    try {
      const result = await resetPassword.mutateAsync({ id: user.id })
      if (result.generatedPassword) {
        setCredential({ username: result.username, password: result.generatedPassword })
      }
    } catch (err) {
      setActionError(err)
    }
  }

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Accounts, the roles they hold, and the projects and sites they can reach."
      />

      {Boolean(actionError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {describeError(actionError)}
        </Alert>
      )}

      <Paper variant="outlined">
        <CrudTable
          query={users}
          rows={users.data?.content ?? []}
          addLabel="Add user"
          describeRow={(u) => u.fullName}
          fields={createFields}
          editFields={editFields}
          columns={[
            {
              label: 'User',
              render: (u) => (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {u.fullName}
                    {u.id === me?.id && (
                      <Chip size="small" label="you" sx={{ ml: 1, height: 18 }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {u.username}
                  </Typography>
                </Box>
              ),
            },
            { label: 'Designation', render: (u) => u.designation ?? '-' },
            {
              label: 'Roles',
              render: (u) =>
                u.roles.length === 0 ? (
                  <Typography variant="caption" color="error">
                    No role
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {u.roles.map((r) => (
                      <Chip key={r.id} size="small" label={r.name} />
                    ))}
                  </Box>
                ),
            },
            { label: 'Scope', render: (u) => SCOPES.find((s) => s.value === u.dataScope)?.label ?? u.dataScope },
            {
              label: 'Reach',
              render: (u) => `${u.projectIds.length} project(s), ${u.siteIds.length} site(s)`,
            },
            { label: 'Permissions', render: (u) => u.permissionCount, align: 'right' },
            {
              label: 'Status',
              render: (u) =>
                u.locked ? <Chip size="small" color="error" label="Locked" /> : <ActiveChip active={u.active} />,
            },
          ]}
          onCreate={canCreate ? handleCreate : undefined}
          onUpdate={
            canUpdate
              ? (row, values) =>
                  update.mutateAsync({
                    id: row.id,
                    body: {
                      fullName: String(values.fullName ?? '').trim(),
                      email: values.email ? String(values.email) : undefined,
                      mobile: values.mobile ? String(values.mobile) : undefined,
                      employeeCode: values.employeeCode ? String(values.employeeCode) : undefined,
                      designation: values.designation ? String(values.designation) : undefined,
                      dataScope: values.dataScope as AdminUser['dataScope'],
                      active: Boolean(values.active),
                    },
                  })
              : undefined
          }
          onDelete={canDelete ? (row) => remove.mutateAsync(row.id) : undefined}
          deleteNote="The account is retired and every session it has ends. Reports and approvals keep the person's name."
          toFormValues={(u) => ({ ...u })}
          rowActions={(u) => (
            <>
              {canAssign && (
                <Tooltip title="Roles and site access">
                  <IconButton size="small" onClick={() => setAccess(u)}>
                    <TuneOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canReset && (
                <Tooltip title="Reset password">
                  <IconButton size="small" onClick={() => void handleReset(u)}>
                    <KeyOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {u.locked && canUpdate && (
                <Tooltip title="Unlock">
                  <IconButton size="small" onClick={() => void unlock.mutateAsync(u.id)}>
                    <LockOpenOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        />
      </Paper>

      {(users.data?.page.totalPages ?? 1) > 1 && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button
            disabled={page + 1 >= (users.data?.page.totalPages ?? 1)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}

      {access && (
        <AccessDialog
          user={access}
          onClose={() => setAccess(null)}
          onSaveRoles={(roleIds) => setRoles.mutateAsync({ id: access.id, roleIds })}
          onSaveScope={(projectIds, siteIds) =>
            setScope.mutateAsync({ id: access.id, projectIds, siteIds })
          }
        />
      )}

      <Dialog open={Boolean(credential)} onClose={() => setCredential(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Password for {credential?.username}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is shown once. Copy it now and hand it over; the account must change it at
            first sign-in.
          </Alert>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 18, userSelect: 'all' }}>
            {credential?.password}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setCredential(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/** Roles and data reach in one place, since they answer the same question together. */
function AccessDialog({
  user,
  onClose,
  onSaveRoles,
  onSaveScope,
}: {
  user: AdminUser
  onClose: () => void
  onSaveRoles: (roleIds: string[]) => Promise<unknown>
  onSaveScope: (projectIds: string[], siteIds: string[]) => Promise<unknown>
}) {
  const { can } = useAuth()
  const roles = useRoles()
  const projects = useMyProjects()
  const sites = useSites({ page: 0, size: 200 })

  const [roleIds, setRoleIds] = useState<string[]>(user.roles.map((r) => r.id))
  const [projectIds, setProjectIds] = useState<string[]>(user.projectIds)
  const [siteIds, setSiteIds] = useState<string[]>(user.siteIds)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  function toggle(list: string[], id: string, set: (next: string[]) => void) {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      if (can('USER:ASSIGN_ROLE')) await onSaveRoles(roleIds)
      if (can('USER:ASSIGN_SCOPE')) await onSaveScope(projectIds, siteIds)
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Access for {user.fullName}</DialogTitle>
      <DialogContent>
        {Boolean(error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {describeError(error)}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          Saving this ends the sessions this person currently has, so the change takes
          effect immediately rather than whenever their token expires.
        </Alert>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Roles
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
          {(roles.data ?? []).map((r) => (
            <FormControlLabel
              key={r.id}
              control={
                <Checkbox
                  checked={roleIds.includes(r.id)}
                  onChange={() => toggle(roleIds, r.id, setRoleIds)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.permissionCodes.length} permissions
                  </Typography>
                </Box>
              }
            />
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Projects
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
          {(projects.data ?? []).map((p) => (
            <FormControlLabel
              key={p.id}
              control={
                <Checkbox
                  checked={projectIds.includes(p.id)}
                  onChange={() => toggle(projectIds, p.id, setProjectIds)}
                />
              }
              label={`${p.code} - ${p.name}`}
            />
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Sites
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {(sites.data?.content ?? []).map((s) => (
            <FormControlLabel
              key={s.id}
              control={
                <Checkbox
                  checked={siteIds.includes(s.id)}
                  onChange={() => toggle(siteIds, s.id, setSiteIds)}
                />
              }
              label={`${s.code} - ${s.name}`}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy}>
          Save access
        </Button>
      </DialogActions>
    </Dialog>
  )
}
